import type { FC, ReactNode } from 'react'
import type { GalleryItem } from '@/types'

import { useCallback, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { GalleryTile } from '@/components/generals/gallery-tile'
import { Lightbox } from '@/components/generals/lightbox'
import { SectionHeading } from '@/components/generals/section-heading'

interface GalleryPreviewProps {
    items: GalleryItem[]
}

const tileLayout = (index: number): string => {
    if (index === 0) return 'col-span-2 row-span-2'
    if (index === 1) return 'row-span-2'
    return ''
}

export const GalleryPreview: FC<GalleryPreviewProps> = ({ items }): ReactNode => {
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const close = useCallback((): void => setOpenIndex(null), [])

    if (items.length === 0) return null

    return (
        <section id='gallery-preview' aria-labelledby='gallery-title' className='surface-night py-24 text-white lg:py-32'>
            <div className='mx-auto max-w-7xl px-6'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        tone='dark'
                        eyebrow='Our gallery'
                        titleId='gallery-title'
                        title={(
                            <>
                                Moments we&apos;ve
                                {' '}
                                <em className='text-gold'>made</em>
                            </>
                        )}
                        description='Mandaps at dusk, garba grounds at midnight and stadiums on match day. A few favourites from our recent work.'
                    />

                    <ButtonLink href='/gallery' variant='outline-light' className='self-start lg:self-auto'>
                        View full gallery
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </ButtonLink>
                </div>

                <div className='mt-14 grid auto-rows-[10rem] grid-cols-2 gap-3 sm:auto-rows-[13rem] md:grid-cols-4 lg:auto-rows-[15rem]'>
                    {items.map((item, index) => (
                        <GalleryTile
                            key={item.id}
                            item={item}
                            onOpen={() => setOpenIndex(index)}
                            sizes={index === 0 ? '(min-width: 768px) 50vw, 100vw' : '(min-width: 768px) 25vw, 50vw'}
                            className={cn(tileLayout(index))}
                        />
                    ))}
                </div>
            </div>

            <Lightbox items={items} index={openIndex} onClose={close} onIndexChange={setOpenIndex} />
        </section>
    )
}
